import { setupWorker } from "msw/browser";
import { handlers } from "./handlers";

export const worker = setupWorker(...handlers);

/**
 * Start the MSW service worker.
 *
 * `onUnhandledRequest` policy:
 *   - any `/api/**` path with no handler → log loudly, return 500.
 *     Spec requirement: "Unmocked requests fail loudly rather than
 *     falling through to a real service."
 *   - static assets, federation remote entries, the bootstrap script
 *     itself, and uiv2's built artifacts → pass through silently. They
 *     resolve against dist/ on the static host.
 *
 * Note: MSW's onUnhandledRequest controls LOGGING and whether the
 * request errors via msw; it does not gate the network call. Static
 * assets that have no handler are served by the static file host as
 * normal.
 */
export async function startMockWorker() {
  await worker.start({
    serviceWorker: { url: "/mockServiceWorker.js" },
    onUnhandledRequest(req: Request, print: { warning(): void; error(): void }) {
      const url = new URL(req.url);

      // Cross-origin (e.g. CDN, Google fonts, the rickroll URL in mtx
      // fixtures) — never our concern.
      if (url.origin !== location.origin) return;

      const isStatic =
        url.pathname.startsWith("/assets/") ||
        url.pathname.startsWith("/themes/") ||
        url.pathname.startsWith("/ui/") ||
        url.pathname === "/msw-bootstrap.js" ||
        url.pathname === "/msw-bootstrap.js.map" ||
        url.pathname === "/mockServiceWorker.js" ||
        url.pathname === "/manifest.json" ||
        url.pathname === "/product-integrations.json" ||
        url.pathname === "/index.html" ||
        url.pathname === "/icon.svg" ||
        url.pathname === "/favicon.ico" ||
        url.pathname === "/" ||
        url.pathname.endsWith("/remoteEntry.js") ||
        url.pathname.endsWith(".js") ||
        url.pathname.endsWith(".css") ||
        url.pathname.endsWith(".svg") ||
        url.pathname.endsWith(".png") ||
        url.pathname.endsWith(".woff") ||
        url.pathname.endsWith(".woff2") ||
        url.pathname.endsWith(".json") ||
        url.pathname.endsWith(".map");

      if (isStatic) return;

      // Anything left should be a /api/** path we forgot to handle —
      // make noise.
      print.error();
    },
  });
  // eslint-disable-next-line no-console
  console.log("[mock-host] MSW worker active — /api/** is fully mocked");
}
